import { useEffect, useRef, useState, useCallback } from 'react';
import { getLatestJwtToken } from '../services/amplify-auth';
import { EventSourcePolyfill } from 'event-source-polyfill';

const MAX_RETRIES = 3;
const RETRY_DELAY = 3000;
const TOKEN_EXPIRED_STATUS = 401;
const HEARTBEAT_TIMEOUT = 20000; // 20 seconds (spec says 15 seconds heartbeat)

export interface OrderAlert {
  // Identifiers
  alertId: string;
  orderId: string;
  orderShortId: string;
  
  // Alert Details
  alertType: AlertType;
  message: string;
  priority: number; // 1-5 (1=low, 5=critical)
  timestamp: string;
  
  // Order Context
  merchantId: number;
  cellId: number;
  landingZoneId?: string;
  
  // State Information
  previousState?: string;
  currentState?: string;
  
  // Additional Data
  metadata: Record<string, any>;
}

export type AlertType = 
  | 'NO_COLLECTOR_ASSIGNED'
  | 'LANDING_PAD_UNASSIGNED'
  | 'TAKEOFF_OVERDUE'
  | 'STATUS_CHANGE'
  | 'COLLECTOR_STATUS_CHANGE'
  | 'READY_FOR_DISPATCH'
  | 'ORDER_CANCELLED'
  | 'DISPATCHED'
  | 'DELIVERED'
  | 'REQUIRES_ATTENTION'
  | 'STATE_TIMEOUT';

interface SSEState {
  isConnected: boolean;
  error: Error | null;
  retryCount: number;
  isRefreshingToken: boolean;
  hasReachedMaxRetries: boolean;
  lastHeartbeatTime: number | null;
  alerts: OrderAlert[];
}

export const useOrderAlertsSSE = (
  cellId: number,
  merchantId?: number,
  enabled: boolean = true,
  useLocalEndpoint: boolean = false // For development/testing
) => {
  
  const EventSource = EventSourcePolyfill;

  const eventSourceRef = useRef<EventSource | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const tokenRef = useRef<string | null>(null);
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout>();

  const [sseState, setSSEState] = useState<SSEState>({
    isConnected: false,
    error: null,
    retryCount: 0,
    isRefreshingToken: false,
    hasReachedMaxRetries: false,
    lastHeartbeatTime: null,
    alerts: []
  });

  const resetConnection = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = undefined;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = undefined;
    }
    setSSEState(prev => ({
      ...prev,
      retryCount: 0,
      hasReachedMaxRetries: false,
      error: null,
      lastHeartbeatTime: null
    }));
  }, []);

  const refreshToken = async () => {
    try {
      setSSEState(prev => ({ ...prev, isRefreshingToken: true }));
      const newToken = await getLatestJwtToken();
      tokenRef.current = newToken;
      return newToken;
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    } finally {
      setSSEState(prev => ({ ...prev, isRefreshingToken: false }));
    }
  };

  // Function to check heartbeat status
  const checkHeartbeatStatus = useCallback(() => {
    setSSEState(prev => {
      if (prev.lastHeartbeatTime) {
        const timeSinceLastHeartbeat = Date.now() - prev.lastHeartbeatTime;
        if (timeSinceLastHeartbeat > HEARTBEAT_TIMEOUT) {
          return {
            ...prev,
            isConnected: false
          };
        }
      }
      return prev;
    });
  }, []);

  // Set up periodic heartbeat checking
  useEffect(() => {
    if (enabled) {
      const interval = setInterval(checkHeartbeatStatus, 2000);
      return () => clearInterval(interval);
    }
  }, [enabled, checkHeartbeatStatus]);

  const connectSSE = useCallback(async (forceTokenRefresh = false) => {
    // Don't attempt to connect if we've reached max retries or if disabled
    if (!enabled) {
      return;
    }

    try {
      // Close existing connection if open
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      let token = tokenRef.current;
      
      // Always get token for authenticated requests
      if (!token || forceTokenRefresh) {
        token = await refreshToken();
        if (forceTokenRefresh) {
          resetConnection();
        }
      }

      // Use Orderful URL from environment
      const baseUrl = process.env.REACT_APP_ORDERFUL_URL;
      
      // Create the URL based on the endpoint type
      let sseUrl: string;
      
      if (merchantId) {
        // Filtered by merchant
        sseUrl = `${baseUrl}/ordercards/alerts/sse/${cellId}/merchant/${merchantId}`;
      } else {
        // Basic authenticated endpoint
        sseUrl = `${baseUrl}/ordercards/alerts/sse/${cellId}`;
      }
      
      console.log('Connecting to Order Alerts SSE:', sseUrl);
      
      // Create headers object for EventSource 
      const eventSourceInitDict: any = {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        heartbeatTimeout: HEARTBEAT_TIMEOUT,
        withCredentials: false
      };

      // Create and set up the EventSource using EventSourcePolyfill to support auth headers
      const eventSource = new EventSourcePolyfill(sseUrl, eventSourceInitDict);
      eventSourceRef.current = eventSource;

      // Handle connection opened
      eventSource.onopen = () => {
        console.log('Order Alerts SSE connection opened');
        setSSEState(prev => ({
          ...prev,
          isConnected: true,
          error: null,
          retryCount: 0,
          hasReachedMaxRetries: false,
          lastHeartbeatTime: Date.now()
        }));
      };

      // Handle connected event
      eventSource.addEventListener('connected', (event) => {
        console.log('Order Alerts SSE connected event received:', event.data);
        setSSEState(prev => ({
          ...prev,
          isConnected: true,
          lastHeartbeatTime: Date.now()
        }));
      });

      // Handle alerts event
      eventSource.addEventListener('alerts', (event) => {
        try {
          const newAlerts: OrderAlert[] = JSON.parse(event.data);
          console.log('Order Alerts received:', newAlerts);
          
          // Update last heartbeat time
          setSSEState(prev => ({
            ...prev,
            isConnected: true,
            lastHeartbeatTime: Date.now(),
            // Prepend new alerts to existing ones
            alerts: [...newAlerts, ...prev.alerts]
          }));
        } catch (error) {
          console.error('Error parsing alerts SSE message:', error);
        }
      });

      // Handle heartbeat event
      eventSource.addEventListener('heartbeat', () => {
        setSSEState(prev => ({
          ...prev,
          isConnected: true,
          lastHeartbeatTime: Date.now()
        }));
      });

      // Handle errors
      eventSource.onerror = (error: any) => {
        if (!error) return;

        console.error('Order Alerts SSE error:', error);
        
        // Check if the error is due to an unauthorized status
        if (!useLocalEndpoint && error.status === TOKEN_EXPIRED_STATUS) {
          try {
            eventSource.close();
            eventSourceRef.current = null;
            // Attempt reconnection with a new token
            connectSSE(true);
            return;
          } catch (reconnectError) {
            console.error('Failed to reconnect after token expiration:', reconnectError);
          }
        }

        setSSEState(prev => ({
          ...prev,
          error: new Error('Order Alerts SSE connection error'),
          isConnected: false
        }));

        // Close the connection on error
        eventSource.close();
        eventSourceRef.current = null;

        // Only attempt retry if we haven't reached max retries
        setSSEState(prev => {
          if (prev.retryCount < MAX_RETRIES && !prev.hasReachedMaxRetries) {
            const nextRetryCount = prev.retryCount + 1;
            const hasReachedMax = nextRetryCount >= MAX_RETRIES;

            if (!hasReachedMax) {
              // Use exponential backoff for retries
              retryTimeoutRef.current = setTimeout(() => {
                connectSSE();
              }, RETRY_DELAY * Math.pow(2, nextRetryCount - 1));
            }

            return {
              ...prev,
              retryCount: nextRetryCount,
              hasReachedMaxRetries: hasReachedMax,
              error: hasReachedMax ? new Error('Max retry attempts reached') : prev.error
            };
          }
          return prev;
        });
      };

    } catch (error) {
      console.error('Error initializing Order Alerts SSE connection:', error);
      setSSEState(prev => ({
        ...prev,
        error: error as Error,
        isConnected: false
      }));
    }
  }, [enabled, cellId, merchantId, useLocalEndpoint, resetConnection]);

  // Initial connection and cleanup
  useEffect(() => {
    if (enabled) {
      resetConnection();
      connectSSE();
    } else if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
      }
    };
  }, [enabled, cellId, merchantId, useLocalEndpoint, connectSSE, resetConnection]);

  // Function to clear alerts
  const clearAlerts = useCallback(() => {
    setSSEState(prev => ({
      ...prev,
      alerts: []
    }));
  }, []);

  // Function to dismiss a specific alert
  const dismissAlert = useCallback((alertId: string) => {
    setSSEState(prev => ({
      ...prev,
      alerts: prev.alerts.filter(alert => alert.alertId !== alertId)
    }));
  }, []);

  return {
    isConnected: sseState.isConnected,
    hasError: sseState.error !== null,
    isRefreshingToken: sseState.isRefreshingToken,
    hasReachedMaxRetries: sseState.hasReachedMaxRetries,
    error: sseState.error,
    alerts: sseState.alerts,
    clearAlerts,
    dismissAlert
  };
};

